import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useContext, useEffect, useState } from "react";
import {
  ButtonGroup,
  ButtonToolbar,
  Col,
  Form,
  InputGroup,
  Row,
} from "react-bootstrap";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import type {
  CashSetting,
  CashTargetType,
} from "../../../api/src/cash-settings/lib";
import Loading from "../Loading";
import { NotificationContext } from "../Notifications";
import AbsolutePercentToggle from "../components/AbsolutePercentToggle";
import ActionButton from "../components/ActionButton";
import ConditionToggle from "../components/ConditionToggle";
import Content from "../components/Content";
import FormError from "../components/FormError";
import FormFieldError from "../components/FormFieldError";
import HelpTooltip from "../components/HelpTooltip";
import SubmitButton from "../components/SubmitButton";
import SymbolField from "../components/SymbolField";
import { extractServerMessage } from "../lib/api";
import {
  cashConditionSchema,
  getSchemaFieldLabel,
  nullableNumberSchema,
  nullableStringSchema,
} from "../lib/forms";
import CashSettingDeleteDialog from "./CashSettingDeleteDialog";
import {
  CASH_SPLIT_REQUIRED,
  CUSTODIAN_CASH_REFUSED,
  CashSettingTemplateHelp,
  cashSplitPercentHelpMessage,
  cashSplitSymbolHelpMessage,
  hasCashSplit,
  isCustodianCashSymbol,
  useCreateCashSetting,
  useQueryCashSetting,
  useQueryCashSettings,
  useUpdateCashSetting,
} from "./lib";

type CashSettingForm = Omit<CashSetting, "id">;

export const schema: yup.ObjectSchema<CashSettingForm> = yup.object({
  name: yup.string().required().label("Name"),
  cashTargetType: yup
    .string()
    .oneOf<CashTargetType>(["absolute", "percent"])
    .default("percent")
    .required()
    .label("Target Type"),
  cashTargetAbsolute: nullableNumberSchema.min(0).label("Cash Target"),
  cashTargetPercent: nullableNumberSchema
    .min(0)
    .max(100)
    .label("Percent Target"),
  cashTargetMinCondition: cashConditionSchema.label("Minimum Condition"),
  cashTargetMinAbsolute: nullableNumberSchema
    .min(0)
    .label("Minimum Cash Trigger"),
  cashTargetMinPercent: nullableNumberSchema
    .min(0)
    .max(100)
    .label("Minimum Percent Trigger"),
  cashTargetMaxCondition: cashConditionSchema.label("Maximum Condition"),
  cashTargetMaxAbsolute: nullableNumberSchema
    .min(0)
    .label("Maximum Cash Trigger"),
  cashTargetMaxPercent: nullableNumberSchema
    .min(0)
    .max(100)
    .label("Maximum Percent Trigger"),
  cashSplitPercent: nullableNumberSchema
    .min(0)
    .max(100)
    .label("Cash Split % of Cash Target"),
  // R11 (doc-cash-split-01a05d5f47df200b) rejects a split percentage with no
  // cash split. Nothing here is read by a rebalance (§4.1), so a firm template
  // carrying the combination is inert — but it pre-fills a pair the account
  // form then refuses, handing the advisor an error the template created.
  // Rejecting it at both entry points removes that (an-decision-01a062423dc5a639).
  //
  // Only this direction is rejected HERE, and that asymmetry against the
  // account form is deliberate. R17, ruled by Jon on 2026-09-16, requires the
  // mirror clause of "the account form", and states the unbalanced cash setting
  // as a case it expects rather than one to stamp out: "a cash setting may name
  // a cash split and carry no percentage of its own". A rebalance reads neither
  // of these columns (§4.1), and R17 supplies a 50% default for the account row
  // that results, so a template carrying a symbol alone seeds a starting value
  // the advisor completes on the account rather than an error the template
  // created. The account form is where both halves are required
  // (`client/src/cash/AccountCashInfo.tsx`).
  cashSplitSymbol: nullableStringSchema
    .label("Cash Split")
    .when("cashSplitPercent", {
      is: (cashSplitPercent: number | null | undefined) =>
        cashSplitPercent !== null && typeof cashSplitPercent !== "undefined",
      // hasCashSplit rather than .required(), because the two forms have to
      // agree with the api on what counts as a cash split and .required() does
      // not trim. nullableStringSchema transforms only undefined, null and ""
      // to null (`client/src/lib/forms.ts`), so "   " satisfied .required() and
      // was posted; `validateCashSplitPair` trims and answered 400 for the same
      // pair this clause is here to catch. A .test() runs for an absent value
      // as readily as for a string, so an empty field is still refused.
      then: (schema) =>
        schema.test({
          name: "cash-split-required-with-split-percentage",
          message: CASH_SPLIT_REQUIRED,
          test: (cashSplitSymbol) => hasCashSplit(cashSplitSymbol),
        }),
      otherwise: (schema) => schema,
    })
    // R15's one named symbol, refused here as well as on the account form.
    // Unlike R17's completeness rule above, this one is not the account's
    // alone: R15 admits any security EXCEPT the one a custodian reports plain
    // cash under (R16), which is a property of the symbol itself rather than
    // of the row it is written to. So `validateCashSplitPair` refuses it for
    // the two `cash_setting` writers as well as for `updateAccount`
    // (`api/src/cash-settings/cash-settings.service.ts`), and without this
    // clause that 400 reached the advisor as the fixed failure toast below
    // with the api's sentence discarded. The account form carries the same
    // clause under the same name (`client/src/cash/AccountCashInfo.tsx`).
    //
    // Refusing the symbol does not drag the completeness rule across with it:
    // a cash split with no percentage beside it still saves here, for the
    // reason given above.
    .test({
      name: "cash-split-is-not-custodian-cash",
      message: CUSTODIAN_CASH_REFUSED,
      test: (cashSplitSymbol) => !isCustodianCashSymbol(cashSplitSymbol),
    }),
});

const CashSettingInfo = () => {
  const {
    register,
    reset,
    watch,
    control,
    getValues,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = useForm<CashSettingForm>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      cashTargetType: "percent",
      cashTargetAbsolute: null,
      cashTargetPercent: null,
      cashTargetMinCondition: "and",
      cashTargetMaxCondition: "and",
    },
  });

  const { cashSettingId: cashSettingIdStr } = useParams();
  const isNew = cashSettingIdStr === "new";
  const cashSettingId =
    typeof cashSettingIdStr === "undefined" || isNew
      ? undefined
      : parseInt(cashSettingIdStr);

  const navigate = useNavigate();

  const {
    isPending: isPendingCashSetting,
    isError: isErrorCashSetting,
    data: dataCashSetting,
  } = useQueryCashSetting(
    typeof cashSettingId === "undefined" ? -1 : cashSettingId,
    {
      enabled: !isNew && typeof cashSettingId !== "undefined",
    },
  );

  useEffect(() => {
    if (typeof dataCashSetting !== "undefined") {
      reset(dataCashSetting);
    }
  }, [dataCashSetting, reset]);

  const createCashSetting = useCreateCashSetting();
  const updateCashSetting = useUpdateCashSetting(cashSettingId ?? -1);

  const notificationContext = useContext(NotificationContext);

  const onSubmit = useCallback(
    async (data: CashSettingForm) => {
      try {
        if (isNew) {
          const newCashSettingId = await createCashSetting.mutateAsync(data);
          notificationContext.pushNotification({
            id: `cash-setting-${newCashSettingId}`,
            header: "Cash Setting Created",
            body: `${data.name} created`,
            variant: "success",
          });
          navigate(`../${newCashSettingId}`);
        } else if (typeof cashSettingId !== "undefined") {
          await updateCashSetting.mutateAsync(data);
          notificationContext.pushNotification({
            id: `cash-setting-${cashSettingId}`,
            header: "Cash Settings Updated",
            body: `${data.name} updated`,
            variant: "success",
          });
        }
      } catch (ex) {
        console.error("Failed to save cash settings", ex);

        // The api's own sentence, when it sent one. `validateCashSplitPair`
        // refuses a cash split for reasons this schema cannot all decide in
        // the browser — whether the symbol is a row in `security_base` is
        // knowable only at the api — and each names this form's own field
        // labels. A fixed body would tell the advisor a save failed without
        // telling them which value to change, or that a value is what failed
        // at all. The two account save handlers read the sentence the same
        // way (`client/src/clients/account/AccountCashBillingSettings.tsx`,
        // `client/src/clients/household/HouseholdAccountCashSettings.tsx`).
        const serverMessage = extractServerMessage(ex);

        // A create has no id yet, so it is keyed "new", as the other create
        // forms key theirs ("contact-new", "household-new").
        notificationContext.pushNotification({
          id: `cash-setting-${cashSettingId ?? "new"}`,
          header: "Failed to Save Cash Settings",
          body: serverMessage ?? `Cash Setting ${data.name} was not saved`,
          variant: "danger",
        });
      }
    },
    [
      cashSettingId,
      createCashSetting,
      isNew,
      navigate,
      notificationContext,
      updateCashSetting,
    ],
  );

  const [showModal, setShowModal] = useState(false);
  const [selectedCashSettingId, setCashSettingId] = useState<number | null>(
    null,
  );
  const handleDeleteClick = useCallback(() => {
    if (typeof cashSettingId !== "undefined") {
      setShowModal(true);
    }
  }, [cashSettingId]);

  const cashTargetType = watch("cashTargetType");

  const {
    isPending: isPendingCashSettings,
    isError: isErrorCashSettings,
    data: dataCashSettings,
  } = useQueryCashSettings({ enabled: !isNew });

  const otherCashSettings = (dataCashSettings?.data || []).filter(
    (cashSetting) =>
      typeof cashSettingId === "undefined" || cashSetting.id !== cashSettingId,
  );

  const cashSettingOptions = isPendingCashSettings
    ? [
        <option key={null} value="">
          Loading...
        </option>,
      ]
    : isErrorCashSettings
      ? [
          <option key={null} value="">
            There was an error loading cash settings
          </option>,
        ]
      : [
          <option key="" value="">
            None
          </option>,
          ...otherCashSettings
            .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
            .map((cashSetting) => (
              <option
                key={cashSetting.id}
                value={cashSetting.id}
                hidden={cashSetting.id === cashSettingId}
              >
                {cashSetting.name}
              </option>
            )),
        ];

  return !isNew && isPendingCashSetting ? (
    <Loading />
  ) : isErrorCashSetting ? (
    <FormError message="Failed to load cash settings" />
  ) : !isNew && !dataCashSetting ? (
    <FormError message="Cash settings not found" />
  ) : (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Row>
        <Col>
          <h1>Cash Setting Details</h1>
        </Col>
        <Col md="auto">
          <ButtonToolbar className="mb-3">
            {isNew ? null : (
              <ButtonGroup className="me-4">
                <ActionButton
                  type="button"
                  label="Delete"
                  icon="/icons/trash.svg"
                  onClick={handleDeleteClick}
                />
              </ButtonGroup>
            )}
            <ButtonGroup>
              <ActionButton
                as={Link}
                to="/cash/settings"
                label="Exit"
                icon="/icons/exit.svg"
              />
            </ButtonGroup>
          </ButtonToolbar>
        </Col>
      </Row>
      <Content>
        <Row>
          <Col xxl={4} xl={4} md={6} className="mb-3">
            <Form.Label>{getSchemaFieldLabel(schema.fields.name)}</Form.Label>
            <Form.Control type="text" {...register("name")} />
            <FormFieldError field={errors.name} />
          </Col>
          <Col xxl={3} xl={4} md={6} className="mb-3">
            <Form.Label>
              {getSchemaFieldLabel(schema.fields.cashTargetAbsolute)}
            </Form.Label>
            <InputGroup>
              {cashTargetType !== "absolute" ? null : (
                <>
                  <Form.Control
                    type="number"
                    min={0}
                    step={0.01}
                    {...register("cashTargetAbsolute")}
                  />
                  <FormFieldError field={errors.cashTargetAbsolute} />
                </>
              )}
              {cashTargetType !== "percent" ? null : (
                <>
                  <Form.Control
                    type="number"
                    min={0}
                    max={100}
                    step={0.01}
                    {...register("cashTargetPercent")}
                  />
                  <FormFieldError field={errors.cashTargetPercent} />
                </>
              )}
              <AbsolutePercentToggle name="cashTargetType" control={control} />
            </InputGroup>
          </Col>
          <Col xl={6} className="mb-3">
            <Form.Label>
              {getSchemaFieldLabel(schema.fields.cashTargetMinAbsolute)}
            </Form.Label>
            <InputGroup>
              <InputGroup.Text>$</InputGroup.Text>
              <Form.Control
                type="number"
                min={0}
                step={0.01}
                {...register("cashTargetMinAbsolute")}
              />
              <ConditionToggle
                name="cashTargetMinCondition"
                control={control}
              />
              <Form.Control
                type="number"
                min={0}
                max={100}
                step={0.01}
                {...register("cashTargetMinPercent")}
              />
              <InputGroup.Text>%</InputGroup.Text>
            </InputGroup>
            <FormFieldError field={errors.cashTargetMinCondition} />
            <FormFieldError field={errors.cashTargetMinAbsolute} />
            <FormFieldError field={errors.cashTargetMinPercent} />
          </Col>
          <Col xl={6} className="mb-3">
            <Form.Label>
              {getSchemaFieldLabel(schema.fields.cashTargetMaxAbsolute)}
            </Form.Label>
            <InputGroup>
              <InputGroup.Text>$</InputGroup.Text>
              <Form.Control
                type="number"
                min={0}
                step={0.01}
                {...register("cashTargetMaxAbsolute")}
              />
              <ConditionToggle
                name="cashTargetMaxCondition"
                control={control}
              />
              <Form.Control
                type="number"
                min={0}
                max={100}
                step={0.01}
                {...register("cashTargetMaxPercent")}
              />
              <InputGroup.Text>%</InputGroup.Text>
            </InputGroup>
            <FormFieldError field={errors.cashTargetMaxCondition} />
            <FormFieldError field={errors.cashTargetMaxAbsolute} />
            <FormFieldError field={errors.cashTargetMaxPercent} />
          </Col>
        </Row>
        <Row>
          <Col xxl={3} xl={4} md={6} className="mb-3">
            <Form.Label>
              {getSchemaFieldLabel(schema.fields.cashSplitPercent)}
              <HelpTooltip
                tooltip={
                  <CashSettingTemplateHelp
                    message={cashSplitPercentHelpMessage}
                  />
                }
                buttonProps={{ width: 16, className: "ms-1 align-text-bottom" }}
              />
            </Form.Label>
            <InputGroup>
              <Form.Control
                type="number"
                min={0}
                max={100}
                step={0.01}
                {...register("cashSplitPercent")}
              />
              <InputGroup.Text>%</InputGroup.Text>
            </InputGroup>
            <FormFieldError field={errors.cashSplitPercent} />
          </Col>
          <Col xxl={3} xl={4} md={6} className="mb-3">
            <Form.Label>
              {getSchemaFieldLabel(schema.fields.cashSplitSymbol)}
              <HelpTooltip
                tooltip={
                  <CashSettingTemplateHelp
                    message={cashSplitSymbolHelpMessage}
                  />
                }
                buttonProps={{ width: 16, className: "ms-1 align-text-bottom" }}
              />
            </Form.Label>
            <SymbolField
              fieldName="cashSplitSymbol"
              register={register}
              control={control}
              getValues={getValues}
            />
            <FormFieldError field={errors.cashSplitSymbol} />
          </Col>
        </Row>
        <Row>
          <Col>
            <SubmitButton isSubmitting={isSubmitting} />
            <CashSettingDeleteDialog
              showModal={showModal}
              setShowModal={setShowModal}
              deleteCashSettingId={
                typeof cashSettingId === "undefined" ? null : cashSettingId
              }
              setDeleteCashSettingId={() => null}
              selectedCashSettingId={selectedCashSettingId}
              setCashSettingId={setCashSettingId}
              cashSettingOptions={cashSettingOptions}
              cashSettingsLoaded={
                !isPendingCashSettings && !isErrorCashSettings
              }
              title="delete-cash-setting"
            />
          </Col>
        </Row>
      </Content>
    </Form>
  );
};

export default CashSettingInfo;
